import groupBy from "lodash-es/groupBy";
import { endOfDay, getUnixTime, startOfDay, subDays, subMonths } from "date-fns/esm";
import { utcToZonedTime } from "date-fns-tz";

import { Percentile } from "ds/components/Charts/types";
import { AspectDataPoint, AspectOutput, AspectPercentile, AspectTimeFilter } from "types/generated";
import { uniqByKey } from "utils/uniq";

type PercentileStyle = Pick<Percentile, "lineColor" | "backgroundColor">;

const successPercentile: PercentileStyle = {
  lineColor: "var(--semantic-color-border-success-emphasized)",
  backgroundColor: "var(--semantic-color-background-decoration-green-subdued)",
};

const warningPercentile: PercentileStyle = {
  lineColor: "var(--semantic-color-border-warning-emphasized)",
  backgroundColor: "var(--semantic-color-background-decoration-yellow-subdued)",
};

const dangerPercentile: PercentileStyle = {
  lineColor: "var(--semantic-color-border-danger-emphasized)",
  backgroundColor: "var(--semantic-color-background-decoration-red-subdued)",
};

export const adaptMetricToChart = (metricData: AspectOutput, keys: string[]) => {
  const { data } = metricData;

  const metricDataArray = data.map(({ timestamp, ...rest }) => ({
    timestamp: Math.round(startOfDay(new Date(timestamp * 1000)).getTime() / 1000),
    ...rest,
  }));

  // Group by 'timestamp'
  const groupedByTimestamp = groupBy(metricDataArray, "timestamp");

  // Map each group to the required format
  const formattedData = Object.entries(groupedByTimestamp).map(([timestamp, dataArray]) => {
    const itemsData = keys.reduce(
      (acc, curr) => {
        acc[curr] = dataArray.find(({ name }) => name === curr)?.value || 0;
        return acc;
      },
      {} as Record<string, number>
    );

    return { timestamp: Number(timestamp), ...itemsData };
  });

  return formattedData;
};

const getPercentileStyle = (index: number): PercentileStyle => {
  if (index === 0) {
    return successPercentile;
  }

  if (index === 1) {
    return warningPercentile;
  }

  return dangerPercentile;
};

export const adaptPercentilesToChart = (percentilesData: AspectPercentile[] = []): Percentile[] => {
  const sortedPercentiles = [...percentilesData].sort((a, b) => a.percentile - b.percentile);

  return sortedPercentiles.map(({ percentile, value }, index) => ({
    percentile,
    value,
    ...getPercentileStyle(index),
  }));
};

export const getChartItems = (metricDataArray: AspectDataPoint[]) => {
  return uniqByKey(metricDataArray, "name").map(({ name }) => name);
};

export const getDefaultTimeFilter = () => {
  const now = new Date();

  // Start of the day a month ago
  const startTime = startOfDay(subMonths(now, 1));

  // End of yesterday
  const endTime = endOfDay(subDays(now, 1));

  return { startTime, endTime };
};

const convertToUTC = (date: Date) => utcToZonedTime(date, "UTC");

export const adaptTimeFilterForApi = (timeFilter: {
  startTime: Date;
  endTime: Date;
}): AspectTimeFilter => {
  const { startTime, endTime } = timeFilter;

  return {
    startTime: getUnixTime(convertToUTC(startTime)),
    endTime: getUnixTime(convertToUTC(endTime)),
  };
};

export const formatMinutes = (minutes: number) => {
  return minutes.toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
};
